/* eslint-disable no-unused-vars */
import { Settings2, X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import {
  isWorkspaceAdminPage,
  workspaceOperationalPageIds,
  workspacePageSections
} from '../../config/pages';
import { CindroBrand } from '../CindroBrand';
import { buildJourneyRadarItem } from '../../utils/journey-radar';
import type { OrganizationOption, PageId } from '../../types/navigation';
import type { WorkspaceState } from '../../types/workspace';

const workspaceOperations = workspacePageSections
  .filter((section) =>
    section.items.some((item) =>
      (workspaceOperationalPageIds as readonly string[]).includes(item.id)
    )
  )
  .map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      (workspaceOperationalPageIds as readonly string[]).includes(item.id)
    )
  }));

const sidebarScrollPositions = new Map<string, number>();

type SidebarProps = {
  onClose?: () => void;
  activePage: PageId;
  activeOrganization?: OrganizationOption;
  workJourneys: WorkspaceState['workJourneys'];
  onSelectNav: (page: PageId) => void;
};

const Sidebar = React.memo(function Sidebar({
  onClose,
  activePage,
  activeOrganization,
  workJourneys,
  onSelectNav
}: SidebarProps) {
  const sidebar = useRef<HTMLElement>(null);
  const scrollKey = activeOrganization?.id || 'workspace:unselected';
  const inboxCount = workJourneys.filter((journey) =>
    Boolean(buildJourneyRadarItem(journey).nextAction)
  ).length;

  useEffect(() => {
    const element = sidebar.current;
    if (!element) return undefined;
    const savedPosition = sidebarScrollPositions.get(scrollKey);
    if (savedPosition !== undefined) element.scrollTop = savedPosition;
    return () => {
      sidebarScrollPositions.set(scrollKey, element.scrollTop);
    };
  }, [scrollKey]);

  return (
    <aside
      ref={sidebar}
      className="sidebar"
      id="primary-navigation"
      onScroll={(event) =>
        sidebarScrollPositions.set(scrollKey, event.currentTarget.scrollTop)
      }
    >
      <div className="sidebarBrand">
        <CindroBrand />
        <button
          type="button"
          className="sidebarClose"
          onClick={onClose}
          aria-label="Close navigation drawer"
        >
          <X size={17} />
        </button>
      </div>

      <div className="navSection navTree">
        {workspaceOperations.length ? (
          <div className="navGroup navScopeGroup navScopeGroup--workspace">
            <div className="navScopeHeading">
              <span>Workspace</span>
              <strong>
                {activeOrganization?.name || 'No workspace selected'}
              </strong>
              <small>Across every project</small>
            </div>
            {workspaceOperations.map((section) => (
              <div key={section.label} className="navGroup navWorkspaceModule">
                <div className="navSectionLabel">{section.label}</div>
                <nav className="navStack" aria-label={section.label}>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = activePage === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={`navRow${active ? ' active' : ''}`}
                        onClick={() => {
                          onSelectNav(item.id);
                          onClose?.();
                        }}
                        title={item.subtitle}
                        aria-current={active ? 'page' : undefined}
                      >
                        <Icon size={15} className="navRowIcon" />
                        <span className="navRowLabel">{item.label}</span>
                        {item.id === 'work-inbox' && inboxCount > 0 ? (
                          <span className="navRowCount">{inboxCount}</span>
                        ) : null}
                      </button>
                    );
                  })}
                </nav>
              </div>
            ))}
            <button
              type="button"
              className={`navRow navRow--manage${isWorkspaceAdminPage(activePage) ? ' active' : ''}`}
              onClick={() => {
                onSelectNav('settings');
                onClose?.();
              }}
              title="Projects, people, access and settings"
              aria-current={
                isWorkspaceAdminPage(activePage) ? 'page' : undefined
              }
            >
              <Settings2 size={15} className="navRowIcon" />
              <span className="navRowLabel">Manage workspace</span>
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
});

export { Sidebar };
